import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

export const LoginForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      console.log("Attempting login with email:", email); // Debug log
      
      if (!email || !password) {
        throw new Error("Email and password are required");
      }

      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password.trim(),
      });

      if (error) throw error;

      console.log("Login successful"); // Debug log
      
      toast({
        title: "Welcome back! 👑",
        description: "Successfully logged in!",
      });
      navigate("/profile");
    } catch (error: any) {
      console.error("Login error:", error); // Debug log
      toast({
        title: "Oops! 😅",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnonymousLogin = async () => {
    setIsLoading(true);
    try {
      console.log("Attempting anonymous login"); // Debug log
      
      const { error } = await supabase.auth.signInWithPassword({
        email: 'anonymous@example.com',
        password: 'anonymous123',
      });

      if (error) throw error;

      console.log("Anonymous login successful"); // Debug log
      
      toast({
        title: "Welcome! 👋",
        description: "Logged in anonymously",
      });
      navigate("/profile");
    } catch (error: any) {
      console.error("Anonymous login error:", error); // Debug log
      toast({
        title: "Oops! 😅",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      onSubmit={handleLogin}
      className="space-y-4 bg-white p-8 rounded-2xl shadow-xl max-w-md w-full mx-auto"
    >
      <Input
        type="email"
        placeholder="Your fabulous email ✨"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="bg-pink-50 border-pink-200 focus:border-pink-400"
        required
      />
      <Input
        type="password"
        placeholder="Your password 🔒"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className="bg-pink-50 border-pink-200 focus:border-pink-400"
        required
      />
      <Button 
        type="submit"
        className="w-full bg-gradient-to-r from-pink-400 to-purple-400 hover:from-pink-500 hover:to-purple-500 text-white font-bold py-3"
        disabled={isLoading}
      >
        {isLoading ? "Logging in..." : "Login Now! 🎉"}
      </Button>
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-white px-2 text-gray-500">Or</span>
        </div>
      </div>
      <Button 
        type="button"
        onClick={handleAnonymousLogin}
        variant="outline"
        className="w-full border-pink-200 hover:bg-pink-50 text-gray-700 font-medium"
        disabled={isLoading}
      >
        {isLoading ? "Please wait..." : "Continue as Guest 🎭"}
      </Button>
    </motion.form>
  );
};